import { motion } from "framer-motion";
import {
  HiArrowRight,
  HiChartBar,
  HiCheckCircle,
  HiLightningBolt,
  HiOutlineCode,
  HiOutlineBriefcase,
  HiOutlineChatAlt2,
  HiOutlineDeviceMobile,
  HiOutlineDesktopComputer,
  HiShieldCheck,
  HiOutlineTrendingUp,
} from "react-icons/hi";
import {
  companyHighlights,
  featuredProjects,
  growthDetails,
  testimonials,
} from "../data/content";
import { fadeInUp, stagger } from "../utils/motion";

const honeyCells = [
  "left-[4%] top-[20%]",
  "left-[12%] top-[38%]",
  "left-[28%] top-[14%]",
  "right-[10%] top-[18%]",
  "right-[4%] top-[42%]",
  "left-[46%] bottom-[16%]",
  "right-[26%] bottom-[10%]",
];

const heroImages = [
  {
    src: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=900&q=80",
    alt: "BeeVerge developers building software together",
  },
  {
    src: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?auto=format&fit=crop&w=700&q=80",
    alt: "BeeVerge product planning workshop",
  },
];

const heroDetails = [
  {
    icon: HiLightningBolt,
    title: "Fast MVP Launch",
    text: "Validate product ideas with focused releases and practical engineering.",
  },
  {
    icon: HiShieldCheck,
    title: "Secure By Design",
    text: "Architecture, APIs, and deployment choices planned for reliability.",
  },
];

const heroServicePreview = [
  {
    icon: HiOutlineDesktopComputer,
    title: "Web Platforms",
    text: "Dashboards, portals, SaaS tools, and business automation systems.",
  },
  {
    icon: HiOutlineDeviceMobile,
    title: "Mobile Apps",
    text: "Fast Android and iOS experiences with launch support.",
  },
  {
    icon: HiOutlineCode,
    title: "Custom Systems",
    text: "APIs, integrations, admin panels, and scalable backend workflows.",
  },
];

function HomeSection() {
  return (
    <section id="home" className="relative min-h-screen overflow-hidden pt-24">
      <div className="pointer-events-none absolute inset-0 bg-hero-grid bg-[length:34px_34px] opacity-25" />
      <motion.div
        className="pointer-events-none absolute inset-x-0 top-20 h-80 bg-gradient-to-r from-yellow-300/10 via-cyan-300/10 to-violet-300/10 blur-3xl"
        animate={{ x: ["-8%", "8%", "-8%"], opacity: [0.35, 0.7, 0.35] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      />
      {honeyCells.map((position, index) => (
        <motion.div
          key={position}
          className={`pointer-events-none absolute hidden h-20 w-20 border border-yellow-200/20 bg-yellow-300/[0.06] lg:block ${position}`}
          style={{ clipPath: "polygon(25% 6%, 75% 6%, 100% 50%, 75% 94%, 25% 94%, 0 50%)" }}
          animate={{
            y: [0, index % 2 === 0 ? -18 : 16, 0],
            rotate: [0, index % 2 === 0 ? 12 : -12, 0],
            opacity: [0.22, 0.55, 0.22],
          }}
          transition={{ duration: 5 + index * 0.35, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
      <motion.div
        className="pointer-events-none absolute left-[6%] top-32 hidden h-40 w-40 rounded-full border border-yellow-300/20 bg-yellow-300/10 blur-sm lg:block"
        animate={{ scale: [1, 1.12, 1], opacity: [0.25, 0.45, 0.25] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="section-shell py-20 sm:py-24">
        <div className="grid items-start gap-10 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
            className="lg:pt-14 xl:pt-20"
          >
            <motion.p
              variants={fadeInUp}
              className="mb-5 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300 sm:text-sm sm:tracking-[0.24em]"
            >
              Innovative IT Company
            </motion.p>

            <motion.h1
              variants={fadeInUp}
              className="text-3xl font-bold leading-tight text-white min-[380px]:text-4xl sm:text-5xl lg:text-6xl"
            >
              BeeVerge Builds Custom Software Solutions
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="mt-6 max-w-2xl text-lg leading-relaxed text-blue-100/80"
            >
              BeeVerge, led by Founder and CEO Harshal Motiram Mahajan, engineers scalable,
              secure, and intelligent digital products that help startups, agencies, and
              enterprises launch faster, automate operations, and grow with confidence.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="mt-8 grid max-w-2xl gap-3 sm:grid-cols-3"
            >
              {companyHighlights.map((item, index) => (
                <motion.div
                  key={item.title}
                  whileHover={{ y: -7, scale: 1.02 }}
                  transition={{ delay: index * 0.04 }}
                  className="rounded-xl border border-cyan-200/15 bg-white/[0.04] p-4 shadow-glass"
                >
                  <HiCheckCircle className="h-5 w-5 text-cyan-300" />
                  <h3 className="mt-3 text-sm font-semibold text-white">{item.title}</h3>
                  <p className="mt-2 text-xs leading-5 text-blue-100/70">{item.text}</p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div variants={fadeInUp} className="mt-9 flex flex-col gap-3 sm:flex-row">
              <motion.a
                href="#contact"
                whileHover={{ scale: 1.05, boxShadow: "0 0 28px rgba(45, 212, 255, 0.45)" }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center justify-center gap-3 rounded-xl border border-cyan-200/30 bg-cyan-300/10 px-5 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-cyan-100 transition sm:px-8 sm:text-sm sm:tracking-[0.18em]"
              >
                Get Started
                <HiArrowRight className="h-5 w-5" />
              </motion.a>
              <motion.a
                href="#services"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] px-5 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-blue-100 transition hover:border-cyan-200/25 sm:px-8 sm:text-sm sm:tracking-[0.18em]"
              >
                View Services
              </motion.a>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="mt-10 max-w-2xl overflow-hidden rounded-2xl border border-cyan-200/15 bg-black/25 p-5 shadow-glass backdrop-blur"
            >
              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
                    Service Preview
                  </p>
                  <h3 className="mt-2 text-2xl font-bold text-white">What We Can Build For You</h3>
                </div>
                <motion.a
                  href="#services"
                  whileHover={{ x: 4 }}
                  className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200 transition hover:text-white"
                >
                  Explore
                  <HiArrowRight className="h-4 w-4" />
                </motion.a>
              </div>

              <div className="mt-5 grid gap-3">
                {heroServicePreview.map(({ icon: Icon, title, text }, index) => (
                  <motion.div
                    key={title}
                    initial={{ opacity: 0, x: -18 }}
                    animate={{ opacity: 1, x: 0 }}
                    whileHover={{ x: 6, borderColor: "rgba(103, 232, 249, 0.45)" }}
                    transition={{ delay: 0.45 + index * 0.1 }}
                    className="flex gap-4 rounded-xl border border-white/10 bg-white/[0.04] p-4"
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-cyan-200/20 bg-cyan-300/10 text-cyan-200">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-white">{title}</h4>
                      <p className="mt-1 text-xs leading-5 text-blue-100/70">{text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-5 grid grid-cols-1 gap-3 min-[420px]:grid-cols-3">
                {["Strategy", "Design", "Development"].map((item, index) => (
                  <motion.div
                    key={item}
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 3 + index * 0.35, repeat: Infinity, ease: "easeInOut" }}
                    className="rounded-xl border border-yellow-200/15 bg-yellow-300/10 px-3 py-3 text-center"
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-yellow-100 sm:text-xs sm:tracking-[0.12em]">
                      {item}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              whileHover={{ y: -6, scale: 1.01 }}
              className="mt-5 max-w-2xl rounded-2xl border border-white/10 bg-white/[0.04] p-5 shadow-glass backdrop-blur"
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-yellow-200">
                    Free Consultation
                  </p>
                  <h3 className="mt-2 text-lg font-bold text-white sm:text-xl">Get a clear project roadmap</h3>
                  <p className="mt-2 text-sm leading-6 text-blue-100/72">
                    Share your idea and we will help define features, timeline, budget, and launch
                    priorities.
                  </p>
                  <div className="mt-4 grid gap-2 sm:grid-cols-2">
                    {[
                      "Feature priority list",
                      "Recommended tech stack",
                      "Timeline and milestone plan",
                      "Launch and support strategy",
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-2 text-xs text-blue-100/78">
                        <HiCheckCircle className="h-4 w-4 shrink-0 text-yellow-200" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <motion.a
                  href="#contact"
                  whileHover={{ x: 4 }}
                  className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-yellow-200/25 bg-yellow-300/10 px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-yellow-100 transition hover:shadow-[0_0_24px_rgba(253,224,71,0.25)]"
                >
                  Book Call
                  <HiArrowRight className="h-4 w-4" />
                </motion.a>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 38 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-5"
          >
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
              className="glass relative overflow-hidden rounded-2xl p-4 shadow-glass"
            >
              <div className="relative overflow-hidden rounded-xl">
                <img
                  src={heroImages[0].src}
                  alt={heroImages[0].alt}
                  className="h-[330px] w-full object-cover sm:h-[390px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05050b] via-[#05050b]/15 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-200">
                    Product Engineering Team
                  </p>
                  <h2 className="mt-2 text-2xl font-bold text-white">
                    Strategy, design, code, launch
                  </h2>
                </div>
              </div>

              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="mt-4 grid gap-4 sm:grid-cols-[0.9fr_1.1fr]"
              >
                <img
                  src={heroImages[1].src}
                  alt={heroImages[1].alt}
                  className="h-44 w-full rounded-xl object-cover"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-cyan-200/15 bg-cyan-300/10 p-4">
                    <p className="text-2xl font-bold text-cyan-100">24h</p>
                    <p className="mt-1 text-xs leading-5 text-blue-100/70">Project response</p>
                  </div>
                  <div className="rounded-xl border border-yellow-200/15 bg-yellow-300/10 p-4">
                    <p className="text-2xl font-bold text-yellow-100">Agile</p>
                    <p className="mt-1 text-xs leading-5 text-blue-100/70">Sprint delivery</p>
                  </div>
                  <div className="col-span-2 rounded-xl border border-white/10 bg-black/25 p-4">
                    <p className="text-sm font-semibold text-white">Built with visible progress</p>
                    <p className="mt-2 text-xs leading-5 text-blue-100/70">
                      Weekly updates, practical milestones, testing checkpoints, and release-ready
                      delivery.
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.7 }}
              className="glass relative overflow-hidden rounded-2xl p-5 shadow-glass sm:p-6"
            >
              <motion.div
                className="pointer-events-none absolute -right-14 -top-14 h-36 w-36 rounded-full bg-yellow-300/15 blur-2xl"
                animate={{ scale: [1, 1.2, 1], opacity: [0.35, 0.75, 0.35] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="pointer-events-none absolute bottom-8 right-8 h-24 w-24 rounded-full border border-cyan-300/20"
                animate={{ rotate: 360 }}
                transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
              >
                <span className="absolute -top-2 left-1/2 h-4 w-4 rounded-full bg-cyan-300 shadow-neon" />
                <span className="absolute bottom-0 right-2 h-3 w-3 rounded-full bg-yellow-300 shadow-[0_0_18px_rgba(253,224,71,0.45)]" />
              </motion.div>
              <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200/20 bg-cyan-300/10 px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100">
                    <motion.span
                      className="h-2 w-2 rounded-full bg-emerald-300 shadow-[0_0_14px_rgba(110,231,183,0.75)]"
                      animate={{ scale: [1, 1.5, 1], opacity: [0.55, 1, 0.55] }}
                      transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                    />
                    Live Delivery Dashboard
                  </div>
                  <h2 className="mt-4 text-3xl font-bold leading-tight text-white">
                    Company Growth Engine
                  </h2>
                  <p className="mt-3 max-w-md text-sm leading-6 text-blue-100/72">
                    Real-time focus on launches, client success, and faster delivery across every
                    product sprint.
                  </p>
                </div>
                <motion.div
                  animate={{ rotate: [0, 8, -8, 0], y: [0, -4, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-cyan-200/25 bg-cyan-300/10 text-cyan-200 shadow-neon"
                >
                  <HiChartBar className="h-6 w-6" />
                </motion.div>
              </div>

              <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
                <div className="rounded-2xl border border-white/10 bg-black/25 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300">
                    What We Monitor
                  </p>
                  <div className="mt-4 grid gap-3">
                    {[
                      "Feature planning, sprint progress, and release readiness",
                      "Design quality, API stability, and performance checkpoints",
                      "Client feedback, support needs, and product improvement ideas",
                    ].map((detail) => (
                      <div key={detail} className="flex gap-3 text-sm leading-6 text-blue-100/78">
                        <HiCheckCircle className="mt-1 h-5 w-5 shrink-0 text-cyan-300" />
                        <span>{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-yellow-200/15 bg-yellow-300/10 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-yellow-200">
                    Delivery Focus
                  </p>
                  <div className="mt-4 space-y-4">
                    {[
                      { label: "Discovery", value: "90%" },
                      { label: "UI/UX Flow", value: "78%" },
                      { label: "Engineering", value: "84%" },
                    ].map((item, index) => (
                      <div key={item.label}>
                        <div className="mb-2 flex justify-between text-xs font-semibold text-blue-100/78">
                          <span>{item.label}</span>
                          <span>{item.value}</span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-black/35">
                          <motion.div
                            className="h-full rounded-full bg-yellow-200"
                            initial={{ width: "12%" }}
                            whileInView={{ width: item.value }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, delay: 0.2 + index * 0.12 }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                {growthDetails.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -6, borderColor: "rgba(103, 232, 249, 0.45)" }}
                  transition={{ delay: 0.25 + index * 0.12 }}
                  className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/25 p-4"
                >
                  <motion.div
                    className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-cyan-300 via-yellow-200 to-violet-300"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 2.4, repeat: Infinity, delay: index * 0.25 }}
                  />
                  <p className="text-3xl font-bold text-cyan-100">{item.value}</p>
                  <p className="mt-2 text-sm font-semibold text-white">{item.label}</p>
                  <p className="mt-2 text-xs leading-5 text-blue-100/65">{item.text}</p>
                  <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-white/10">
                    <motion.div
                      className="h-full rounded-full bg-cyan-300"
                      initial={{ width: "18%" }}
                      whileInView={{ width: `${74 + index * 8}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.2, delay: 0.25 + index * 0.12 }}
                    />
                  </div>
                </motion.div>
                ))}
              </div>

              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="mt-5 rounded-xl border border-cyan-200/15 bg-cyan-300/10 p-4"
              >
                <div className="flex items-center gap-3">
                  <HiOutlineTrendingUp className="h-5 w-5 text-cyan-200" />
                  <p className="text-sm font-semibold text-white">Built for steady business growth</p>
                </div>
                <p className="mt-2 text-sm leading-6 text-blue-100/72">
                  From MVP planning to enterprise modernization, BeeVerge helps teams build software
                  that keeps improving after launch.
                </p>
              </motion.div>

              <div className="mt-5 grid grid-cols-1 gap-3 min-[420px]:grid-cols-3">
                {["Plan", "Build", "Launch"].map((step, index) => (
                  <motion.div
                    key={step}
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 3 + index * 0.4, repeat: Infinity, ease: "easeInOut" }}
                    className="relative overflow-hidden rounded-xl border border-yellow-200/15 bg-yellow-300/10 px-3 py-4 text-center"
                  >
                    <motion.span
                      className="absolute inset-x-0 bottom-0 h-px bg-yellow-200"
                      animate={{ opacity: [0.2, 1, 0.2] }}
                      transition={{ duration: 1.8, repeat: Infinity, delay: index * 0.25 }}
                    />
                    <p className="text-lg font-bold text-yellow-200">0{index + 1}</p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-blue-100/75">
                      {step}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mt-12 grid gap-5 md:grid-cols-2"
        >
          {heroDetails.map(({ icon: Icon, title, text }, index) => (
            <motion.article
              key={title}
              variants={fadeInUp}
              whileHover={{ y: -8, scale: 1.01 }}
              transition={{ delay: index * 0.08 }}
              className="glass relative overflow-hidden rounded-2xl p-6 shadow-glass"
            >
              <motion.div
                className="absolute inset-x-0 -top-20 h-28 bg-cyan-300/10 blur-2xl"
                animate={{ x: ["-20%", "20%", "-20%"] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              />
              <div className="relative flex gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-cyan-200/25 bg-cyan-300/10 text-cyan-200">
                  <Icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-blue-100/75">{text}</p>
                </div>
              </div>
            </motion.article>
          ))}
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mt-16 grid gap-5 lg:grid-cols-[1.1fr_0.9fr]"
        >
          <motion.div variants={fadeInUp} className="glass rounded-2xl p-6 shadow-glass">
            <div className="mb-5 flex items-center gap-3">
              <HiOutlineBriefcase className="h-6 w-6 text-cyan-300" />
              <h2 className="text-2xl font-bold text-white">Project Experience</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {featuredProjects.map((project, index) => (
                <motion.article
                  key={project.title}
                  whileHover={{ y: -6, scale: 1.02 }}
                  transition={{ delay: index * 0.04 }}
                  className="rounded-xl border border-white/10 bg-black/25 p-4 transition hover:border-cyan-200/30 hover:bg-cyan-300/10"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300">
                    {project.type}
                  </p>
                  <h3 className="mt-3 text-base font-semibold text-white">{project.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-blue-100/75">{project.result}</p>
                </motion.article>
              ))}
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="glass rounded-2xl p-6 shadow-glass">
            <div className="mb-5 flex items-center gap-3">
              <HiOutlineChatAlt2 className="h-6 w-6 text-cyan-300" />
              <h2 className="text-2xl font-bold text-white">Client Testimonials</h2>
            </div>
            <div className="space-y-4">
              {testimonials.map((testimonial) => (
                <motion.blockquote
                  key={testimonial.name}
                  whileHover={{ x: 6 }}
                  className="rounded-xl border border-white/10 bg-black/25 p-4"
                >
                  <p className="text-sm leading-6 text-blue-100/82">"{testimonial.quote}"</p>
                  <footer className="mt-3">
                    <p className="text-sm font-semibold text-white">{testimonial.name}</p>
                    <p className="text-xs text-cyan-200">{testimonial.company}</p>
                  </footer>
                </motion.blockquote>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        className="pointer-events-none absolute right-[8%] top-[26%] hidden h-32 w-32 rounded-full border border-cyan-300/30 bg-cyan-300/10 lg:block"
        animate={{ y: [0, -14, 0], rotate: [0, 10, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="pointer-events-none absolute bottom-[20%] right-[20%] hidden h-16 w-16 rounded-md border border-violet-300/40 bg-violet-300/10 lg:block"
        animate={{ y: [0, 18, 0], rotate: [0, -12, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      />
    </section>
  );
}

export default HomeSection;
